import { FormEvent, useState } from "react";
import { WalletCards } from "lucide-react";
import { Modal } from "../Modal";
import { Field, FormSection } from "../FormLayout";
import { formatSchoolDate } from "../students/studentDisplay";
import { apiFieldErrors } from "../../utils/apiError";
import type { Position, StaffProfile } from "../../services/staffService";
import { StaffStatus } from "./StaffStatus";

const FORM_ID = "edit-employment-form";
const FORM_FIELDS = [
  "position", "hire_date", "employment_status", "work_location", "currency",
  "payment_frequency", "tin", "nssf_number", "tax_residency", "bank_name",
  "bank_branch", "bank_account_name", "bank_account_number",
] as const;
// Ending employment has its own form, which revokes the login and releases
// assignments; this one only moves between the working statuses.
const WORKING_STATUSES = ["active", "probation", "suspended"];

type Props = {
  profile: StaffProfile;
  positions: Position[];
  isSaving: boolean;
  error: unknown;
  onSubmit: (data: Partial<StaffProfile>) => void;
  onPayChange: () => void;
  onClose: () => void;
};

export default function EditEmploymentModal({
  profile,
  positions,
  isSaving,
  error,
  onSubmit,
  onPayChange,
  onClose,
}: Props) {
  const [accountNumber, setAccountNumber] = useState(profile.bank_account_number || "");
  const { fields: errors, message } = apiFieldErrors(
    error,
    FORM_FIELDS,
    "The employment record could not be saved. Please try again.",
  );
  const hasEnded = !WORKING_STATUSES.includes(profile.employment_status);
  const money = (amount?: string | number | null) =>
    `${profile.currency} ${Number(amount ?? 0).toLocaleString()}`;
  const allowances = Number(profile.allowances ?? 0);
  const today = new Date().toISOString().slice(0, 10);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    onSubmit({
      position: Number(form.get("position")),
      hire_date: text("hire_date"),
      employment_status: text("employment_status"),
      work_location: text("work_location"),
      currency: text("currency"),
      payment_frequency: text("payment_frequency") as StaffProfile["payment_frequency"],
      tin: text("tin"),
      nssf_number: text("nssf_number"),
      tax_residency: text("tax_residency") as StaffProfile["tax_residency"],
      bank_name: text("bank_name"),
      bank_branch: text("bank_branch"),
      bank_account_name: text("bank_account_name"),
      bank_account_number: text("bank_account_number"),
    });
  };

  return (
    <Modal
      isOpen
      size="3xl"
      title="Edit employment"
      onClose={() => {
        if (!isSaving) onClose();
      }}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={isSaving} className="btn btn-outline">
            Cancel
          </button>
          <button type="submit" form={FORM_ID} disabled={isSaving} className="btn btn-primary">
            {isSaving ? "Saving…" : "Save employment"}
          </button>
        </>
      }
    >
      <form id={FORM_ID} key={profile.id} onSubmit={submit} className="font-refined space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <div>
            <p className="font-semibold text-gray-900">{profile.user_name}</p>
            <p className="font-mono text-xs text-gray-500">
              {profile.employee_id} · {profile.user_username}
            </p>
          </div>
          <StaffStatus status={profile.employment_status} />
        </div>

        {message && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {message}
          </p>
        )}

        <FormSection title="Role & placement">
          <Field label="Position *" name="position" errors={errors}>
            <select required name="position" defaultValue={profile.position} className="input mt-1">
              {positions
                .filter((position) => position.is_active || position.id === profile.position)
                .map((position) => (
                  <option key={position.id} value={position.id}>
                    {position.title} · {position.department_name || "School-wide"}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Hire date *" name="hire_date" errors={errors}>
            <input
              required
              type="date"
              max={today}
              name="hire_date"
              defaultValue={profile.hire_date}
              className="input mt-1"
            />
          </Field>
          <Field
            label="Employment status *"
            name="employment_status"
            errors={errors}
            hint={
              hasEnded
                ? `Employment ended${profile.employment_changes?.[0] ? ` on ${formatSchoolDate(profile.employment_changes[0].effective_date)}` : ""}. Choosing a working status re-employs them.`
                : "To end employment, use End employment instead."
            }
          >
            <select
              required
              name="employment_status"
              defaultValue={profile.employment_status}
              className="input mt-1"
            >
              {hasEnded && (
                <option value={profile.employment_status} className="capitalize">
                  {profile.employment_status.replace(/_/g, " ")} (current)
                </option>
              )}
              <option value="active">Active</option>
              <option value="probation">Probation</option>
              <option value="suspended">Suspended</option>
            </select>
          </Field>
          <Field label="Work location" name="work_location" errors={errors}>
            <input
              name="work_location"
              maxLength={100}
              defaultValue={profile.work_location || ""}
              className="input mt-1"
              placeholder="e.g. Main campus"
            />
          </Field>
        </FormSection>

        <FormSection
          title="Pay"
          description="Basic pay and allowances change only through an approved pay change."
        >
          <div className="rounded-lg border border-gray-200 sm:col-span-2">
            <dl className="grid grid-cols-3 divide-x divide-gray-200 text-center">
              {[
                ["Basic pay", profile.salary ? money(profile.salary) : "Not set"],
                ["Allowances", allowances > 0 ? money(allowances) : "None"],
                [
                  "Gross",
                  profile.salary ? money(Number(profile.salary) + allowances) : "—",
                ],
              ].map(([label, value]) => (
                <div key={label} className="px-3 py-2.5">
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
                  <dd className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <span>
                {profile.allowance_lines?.length
                  ? profile.allowance_lines
                      .map((line) => `${line.name} ${Number(line.amount).toLocaleString()}`)
                      .join(" · ")
                  : "No named allowances."}
              </span>
              <button
                type="button"
                onClick={onPayChange}
                className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline"
              >
                <WalletCards className="h-3.5 w-3.5" />
                {profile.salary ? "Request pay change" : "Set pay for approval"}
              </button>
            </div>
          </div>
          <Field label="Currency *" name="currency" errors={errors}>
            <select required name="currency" defaultValue={profile.currency || "UGX"} className="input mt-1">
              <option value="UGX">UGX</option>
            </select>
          </Field>
          <Field label="Paid *" name="payment_frequency" errors={errors}>
            <select
              required
              name="payment_frequency"
              defaultValue={profile.payment_frequency || "monthly"}
              className="input mt-1"
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </Field>
        </FormSection>

        <FormSection
          title="Payroll identifiers"
          description="Shown on payslips. Optional: a payroll run lists anyone missing details rather than skipping them."
        >
          <Field label="TIN" name="tin" errors={errors}>
            <input
              name="tin"
              defaultValue={profile.tin || ""}
              className="input mt-1 font-mono"
              placeholder="URA Tax Identification Number"
            />
          </Field>
          <Field label="NSSF number" name="nssf_number" errors={errors}>
            <input
              name="nssf_number"
              defaultValue={profile.nssf_number || ""}
              className="input mt-1 font-mono"
              placeholder="NSSF membership number"
            />
          </Field>
          <Field
            label="Tax residency"
            name="tax_residency"
            errors={errors}
            hint="Non-residents pay PAYE on their own bands, from the first shilling."
          >
            <select
              name="tax_residency"
              defaultValue={profile.tax_residency || "resident"}
              className="input mt-1"
            >
              <option value="resident">Resident</option>
              <option value="non_resident">Non-resident</option>
            </select>
          </Field>
        </FormSection>

        <FormSection title="Bank details" description="Where this person's pay is sent.">
          <Field
            label={accountNumber.trim() ? "Bank *" : "Bank"}
            name="bank_name"
            errors={errors}
            hint={accountNumber.trim() ? "Needed because an account number is given." : undefined}
          >
            <input
              name="bank_name"
              required={Boolean(accountNumber.trim())}
              defaultValue={profile.bank_name || ""}
              className="input mt-1"
              placeholder="e.g. Stanbic Bank"
            />
          </Field>
          <Field label="Branch" name="bank_branch" errors={errors}>
            <input name="bank_branch" defaultValue={profile.bank_branch || ""} className="input mt-1" />
          </Field>
          <Field
            label="Account name"
            name="bank_account_name"
            errors={errors}
            hint="Only if it differs from their own name."
          >
            <input
              name="bank_account_name"
              defaultValue={profile.bank_account_name || ""}
              className="input mt-1"
            />
          </Field>
          <Field
            label="Account number"
            name="bank_account_number"
            errors={errors}
            hint="Spaces are removed when saved."
          >
            <input
              name="bank_account_number"
              value={accountNumber}
              onChange={(event) => setAccountNumber(event.target.value)}
              inputMode="numeric"
              autoComplete="off"
              className="input mt-1 font-mono"
            />
          </Field>
        </FormSection>
      </form>
    </Modal>
  );
}
